import { AnalyticsContext } from '@carvana/eventable-clickstream';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import EventName from '~/constants/vdp/EventName';
import GalleryImageType from '~/constants/vdp/galleryImageType';
import GalleryType from '~/constants/vdp/GalleryType';
import ModalContext from '~/context/vdp/ModalContext';

const defaultContext = {
  closeFullScreenGallery: () => {},
  openFullScreenGallery: (_imgId: string, _imgType: GalleryImageType) => {},
  setSlideImgId: (_imgId: string) => {},
  slideImgId: ''
};

type ContextType = typeof defaultContext;

const CarouselContext = createContext<ContextType>(defaultContext);

export function CarouselContextProvider({ children }: React.PropsWithChildren) {
  const { trackEvent } = useContext(AnalyticsContext);
  const { setFullScreenGalleryOpen } = useContext(ModalContext);

  const [slideImgId, setSlideImgId] = useState('');

  const openFullScreenGallery = useCallback(
    (imgId: string, imgType: GalleryImageType) => {
      setSlideImgId(imgId);
      setFullScreenGalleryOpen(true);
      trackEvent('VDP - Gallery - Clicked into full-screen mode');
      trackEvent(EventName.galleryEngaged, {
        galleryType: GalleryType.FullScreen,
        imageType: imgType
      });
    },
    [setFullScreenGalleryOpen, trackEvent]
  );

  const closeFullScreenGallery = useCallback(() => {
    setFullScreenGalleryOpen(false);
    trackEvent('Close fullscreen gallery');
  }, [setFullScreenGalleryOpen, trackEvent]);

  const value = useMemo<ContextType>(
    (): ContextType => ({
      closeFullScreenGallery,
      openFullScreenGallery,
      setSlideImgId,
      slideImgId
    }),
    [closeFullScreenGallery, openFullScreenGallery, slideImgId]
  );

  return (
    <CarouselContext.Provider value={value}>
      {children}
    </CarouselContext.Provider>
  );
}

CarouselContextProvider.displayName = 'CarouselContextProvider';

export default CarouselContext;
